import React, { useState, useEffect, useReducer, useContext } from "react";
import NumberFormat from "react-number-format";
import { makeStyles } from "@material-ui/core/styles";
import Backdrop from "@material-ui/core/Backdrop";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PersonIcon from "@material-ui/icons/Person";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import logger from "../logger";
import { API, graphqlOperation } from "aws-amplify";
import { AppContext } from "../views/App";
import { CART_STATUS, ORDER_STATUS } from "../constants";
import { createCart, updateCart } from "../graphql/mutations";
import { validateEmail, toTitleCase } from "../functions";

const initialState = {
  email: "",
  firstname: "",
  lastname: "",
  phone: "",
  product: "",
  price: "",
  note: "",
  error: {
    email: false,
    firstname: false,
    lastname: false,
    phone: false,
    product: false,
    price: false,
    note: false
  }
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "email":
      return {
        ...state,
        [type]: payload,
        error: { ...state.error, email: !validateEmail(payload) }
      };
    case "firstname":
    case "lastname":
    case "phone":
    case "product":
    case "price":
    case "note":
      return { ...state, [type]: payload };
    case "load":
      return {
        ...state,
        firstname: payload.firstname,
        lastname: payload.lastname,
        phone: payload.phone
      };
    case "error":
      return {
        ...state,
        error: { ...state.error, [payload.prop]: payload.value }
      };
    case "reset":
      return {
        ...initialState
      };
    default:
      return { ...state };
  }
};

const useStyles = makeStyles(theme => ({
  backButton: {
    marginRight: theme.spacing(2)
  },
  historyButton: {
    marginLeft: theme.spacing(2)
  },
  title: {
    flexGrow: 1
  },
  container: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    overflow: "auto"
  },
  summary: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  estado: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  cardHeader: {
    paddingBottom: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  cardActions: {
    justifyContent: "flex-end"
  },
  comprobante: {
    backgroundColor: theme.palette.grey[100]
  }
}));

const PedidoNew = ({ mobile, onClose, onCreate }) => {
  const classes = useStyles();
  const { cuenta, store } = useContext(AppContext);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [sending, setSending] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [cart, setCart] = useState(null);

  useEffect(() => {
    const fetchContactos = async () => {
      const tempContactos = [];

      try {
        const init = {
          queryStringParameters: {
            cuenta: cuenta.id_cuenta
          }
        };

        const result = await API.get(process.env.REACT_APP_HUB_API, "/contactos", init);

        if (result.success) {
          for (let i = 0; i < result.data.length; i++) {
            const item = result.data[i];
            const body_contacto = JSON.parse(item.body_contacto);

            tempContactos.push({ ...item, body_contacto });
          }
        }
      } catch (err) {
        logger("PedidoNew.fetchContactos.error", err);
      } finally {
        setContactos(tempContactos);
        return true;
      }
    };

    if (cuenta) {
      fetchContactos();
    }
  }, [cuenta]);

  const validate = () => {
    return (
      validateEmail(state.email) &&
      state.firstname.trim() !== "" &&
      state.lastname.trim() !== "" &&
      state.phone.trim() !== "" &&
      state.product.trim() !== "" &&
      state.price > 0
    );
  };

  const validateField = field => {
    dispatch({
      type: "error",
      payload: { prop: field, value: state[field].toString().trim() === "" }
    });
  };

  const saveCart = async (cartID, params) => {
    let operation = "";
    let graphql = null;
    let vars = null;

    if (!cartID) {
      vars = {
        input: {
          createdAt: new Date().toISOString(),
          status: CART_STATUS.onPayment,
          storeID: store.id,
          clientID: state.email,
          items: JSON.stringify([
            {
              quantity: 1,
              product: {
                name: state.product
              },
              price: state.price,
              total: state.price
            }
          ]),
          ...params
        }
      };

      operation = "createCart";
      graphql = createCart;
    } else {
      vars = {
        input: {
          id: cartID,
          ...params
        }
      };

      operation = "updateCart";
      graphql = updateCart;
    }

    try {
      const result = await API.graphql(graphqlOperation(graphql, vars));

      if (result.data[operation]) {
        setCart(result.data[operation]);
        return result.data[operation];
      } else {
        logger(`PedidoNew.saveCart.result.${operation}`, result);
      }
    } catch (err) {
      logger(`PedidoNew.saveCart.${operation}.error`, err);
    }

    return null;
  };

  const createOrder = (carrito, status) => {
    const cliente = {
      primer_nombre: state.firstname,
      apellido_paterno: state.lastname,
      telefono: state.phone,
      email: state.email
    };

    const tienda = {
      id: store.id,
      slug: store.slug,
      hostname: store.hostname,
      nombre: store.name,
      url: window.location.href,
      email: store.body.config.email
    };

    const pago = {
      subtotal: state.price,
      total: state.price,
      medioPago: null
    };

    const order = {
      origen: "shop",
      estado: status,
      nota: state.note && null,
      cliente,
      carrito,
      tienda,
      pago
    };

    return order;
  };

  const processClient = async () => {
    if (validateEmail(state.email)) {
      dispatch({ type: "load", payload: "email" });
      const client = contactos.find(contacto => contacto.body_contacto.email === state.email);

      if (client) {
        dispatch({
          type: "load",
          payload: {
            firstname: client.body_contacto.primer_nombre,
            lastname: client.body_contacto.apellido_paterno,
            phone: client.body_contacto.telefono
          }
        });
      } else {
        dispatch({
          type: "load",
          payload: {
            firstname: "",
            lastname: "",
            phone: ""
          }
        });
      }
    } else {
      dispatch({ type: "error", payload: { prop: "email", value: true } });
    }
  };

  const processOrder = async () => {
    if (validate()) {
      setSending(true);

      const currentCart = await saveCart(cart ? cart.id : null, null);

      let ticket = null;
      let keepLoading = false;

      if (currentCart) {
        const order = createOrder(currentCart, ORDER_STATUS.inbox);

        if (currentCart.ticket) {
          if (typeof currentCart.ticket === "string") {
            ticket = JSON.parse(currentCart.ticket);
          } else {
            ticket = currentCart.ticket;
          }
        } else {
          ticket = await send(order, currentCart.id, CART_STATUS.onPayment);
        }

        if (ticket) {
          onCreate(ticket.ticketId);
        } else {
          logger("PedidoNew.processOrder.ticket.error");
        }
      } else {
        logger("PedidoNew.processOrder.save.error");
      }

      if (!keepLoading) {
        setSending(false);
      }
    }
  };

  const send = async (order, cartID, status) => {
    setSending(true);

    try {
      const response = await API.post(process.env.REACT_APP_HUB_API, "/tickets/venta", {
        body: order
      });

      if (response && response.success) {
        const ticket = response.ticket;

        if (ticket.success) {
          saveCart(cartID, {
            orderNumber: `${ticket.folio}-${store.code}`,
            ticket: JSON.stringify({
              ...ticket,
              origen: "shop"
            }),
            status
          });

          return ticket;
        }
      } else {
        throw response;
      }
    } catch (err) {
      logger("PedidoNew.sending.error", err);
      setSending(false);
    }

    return null;
  };

  const Fecha = () => {
    return <Typography className="p-2" component="p" variant="caption" align="right"></Typography>;
  };

  const Cliente = () => {
    return (
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Correo"
              className={classes.control}
              placeholder="Ej: correo@gmail.com"
              disabled={sending}
              size="small"
              variant="standard"
              value={state.email}
              onChange={({ target }) => dispatch({ type: "email", payload: target.value })}
              onBlur={processClient}
              error={state.error.email}
              helperText={state.error.email && "Debe ingresar un correo válido"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Nombre"
              className={classes.control}
              placeholder="Ej: Pablo"
              disabled={sending}
              size="small"
              variant="standard"
              value={state.firstname && toTitleCase(state.firstname)}
              onChange={({ target }) => dispatch({ type: "firstname", payload: target.value })}
              onBlur={() => validateField("firstname")}
              error={state.error.firstname}
              helperText={state.error.firstname && "Debe ingresar el nombre"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Apellido"
              className={classes.control}
              placeholder="Ej: Pérez"
              disabled={sending}
              size="small"
              variant="standard"
              value={state.lastname && toTitleCase(state.lastname)}
              onChange={({ target }) => dispatch({ type: "lastname", payload: target.value })}
              onBlur={() => validateField("lastname")}
              error={state.error.lastname}
              helperText={state.error.lastname && "Debe ingresar el apellido"}
            />
          </Grid>

          <Grid item xs={12}>
            <NumberFormat
              customInput={TextField}
              required
              fullWidth
              format="+56 9 #### ####"
              allowEmptyFormatting
              type="tel"
              value={state.phone}
              disabled={sending}
              onValueChange={({ _, value }) => dispatch({ type: "phone", payload: value })}
              onBlur={() => validateField("phone")}
              error={state.error.phone}
              helperText={state.error.phone && "Debe ingresar el teléfono"}
            />
          </Grid>
        </Grid>
      </Container>
    );
  };

  const Detalle = () => {
    return (
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Producto o servicio"
              className={classes.control}
              placeholder="Ej: Curso"
              disabled={sending}
              size="small"
              variant="standard"
              value={state.product}
              onChange={({ target }) => dispatch({ type: "product", payload: target.value })}
              onBlur={() => validateField("product")}
              error={state.error.product}
              helperText={state.error.product && "Debe ingresar el producto o servicio"}
            />
          </Grid>

          <Grid item xs={12}>
            <NumberFormat
              fullWidth
              thousandSeparator="."
              decimalSeparator=","
              prefix="$"
              allowNegative={false}
              customInput={TextField}
              required
              label="Precio"
              placeholder="Ej: $7.000"
              disabled={sending}
              value={state.price}
              size="small"
              inputProps={{
                maxLength: 10
              }}
              onValueChange={({ _, value }) =>
                dispatch({ type: "price", payload: parseInt(value) })
              }
              onBlur={() => validateField("price")}
              error={state.error.price}
              helperText={state.error.price && "Debe ingresar el precio"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nota"
              className={classes.control}
              placeholder="Ej: Dejar en conserjería"
              disabled={sending}
              size="small"
              variant="standard"
              value={state.note}
              onChange={({ target }) => dispatch({ type: "note", payload: target.value })}
            />
          </Grid>
        </Grid>
      </Container>
    );
  };

  return (
    <>
      <Backdrop className="backdrop" open={sending}>
        <CircularProgress />
      </Backdrop>

      <div>
        <AppBar position="sticky">
          <Toolbar>
            {onClose && (
              <IconButton
                className={classes.backButton}
                edge="start"
                color="inherit"
                onClick={onClose}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Typography className={classes.title} variant="h6">
              Nuevo pedido
            </Typography>
          </Toolbar>
        </AppBar>

        <Container className={classes.container}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Fecha />
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <CardHeader
                      className={classes.cardHeader}
                      avatar={<PersonIcon />}
                      title="Cliente"
                    />
                    <CardContent className={classes.cardContent}>{Cliente()}</CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  className={classes.cardHeader}
                  avatar={<ShoppingCartIcon />}
                  title={`Detalle`}
                />
                <CardContent className={classes.cardContent}>{Detalle()}</CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={processOrder}>
                Aceptar
              </Button>
              <Button onClick={() => dispatch({ type: "reset" })}>Cancelar</Button>
            </Grid>
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default PedidoNew;
